import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useCursor } from "@/context/CursorContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProjectCard } from "@/components/ProjectCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const ShowcasePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { setCursor, reset } = useCursor();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    document.documentElement.scrollTo({ top: 0, behavior: "auto" });
    setLoading(true);
    setNotFound(false);
    axios
      .get(`${API}/showcases/${slug}`)
      .then((res) => {
        const d = res.data;
        // If the showcase has a single video, jump straight to the case study so it autoplays.
        if (d && Array.isArray(d.videos) && d.videos.length === 1) {
          navigate(`/case/${d.videos[0].slug}?autoplay=1`, { replace: true });
          return;
        }
        setData(d);
      })
      .catch((e) => {
        console.error("showcase fetch failed", e);
        if (e.response && e.response.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  return (
    <div className="relative bg-void min-h-screen">
      <Navbar onNavigate={(k) => (k === "work" ? navigate("/work") : navigate(`/#${k}`))} />

      <main className="mx-auto max-w-[1600px] px-6 md:px-12 pt-40 pb-32">
        <Link
          to="/work"
          onMouseEnter={() => setCursor("link")}
          onMouseLeave={reset}
          data-testid="back-to-work"
          className="group inline-flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] uppercase text-bone/50 hover:text-bone transition-colors duration-300"
        >
          <span className="transition-transform duration-300 group-hover:-translate-x-1">←</span>
          <span>All work</span>
        </Link>

        {loading && (
          <p className="mt-24 font-mono text-sm text-bone/40 tracking-widest uppercase text-center">
            Loading…
          </p>
        )}

        {notFound && (
          <div className="mt-24 text-center">
            <p className="font-mono text-sm text-bone/40 tracking-widest uppercase">
              Showcase not found.
            </p>
            <Link
              to="/work"
              className="mt-8 inline-block font-mono text-[11px] tracking-[0.3em] uppercase text-bone underline"
            >
              Back to all work
            </Link>
          </div>
        )}

        {data && !loading && (
          <>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE }}
              className="mt-10"
            >
              <span className="font-mono text-[11px] tracking-[0.35em] uppercase text-bone/40">
                Client
              </span>
              <h1
                data-testid="showcase-title"
                className="font-work mt-6 text-5xl md:text-8xl lg:text-9xl font-bold uppercase leading-[0.85] text-bone break-words [overflow-wrap:anywhere]"
              >
                {data.name}
              </h1>
              {data.description && (
                <p className="mt-8 max-w-2xl font-body font-light text-lg text-bone/60 leading-relaxed whitespace-pre-wrap">
                  {data.description}
                </p>
              )}
              <p className="mt-6 font-mono text-[11px] tracking-[0.25em] uppercase text-bone/40">
                {data.videos.length} {data.videos.length === 1 ? "piece" : "pieces"}
              </p>
            </motion.div>

            <div className="mt-20 grid grid-cols-1 md:grid-cols-12 gap-x-8 gap-y-16 md:gap-y-24">
              {data.videos.map((p, i) => (
                <ProjectCard key={p.slug} project={p} index={i} onOpen={(pr) => navigate(`/case/${pr.slug}`)} />
              ))}
            </div>

            {data.videos.length === 0 && (
              <p className="mt-16 font-mono text-sm text-bone/40 tracking-widest uppercase text-center">
                This showcase has no videos yet.
              </p>
            )}
          </>
        )}
      </main>

      <Footer onNavigate={(k) => navigate(`/#${k}`)} />
    </div>
  );
};

export default ShowcasePage;
